/**
 * The active site role of this session, for the account menu and the 403 panel (plan
 * 2026-10-22-cp-active-role, design direction section 5).
 *
 * Who renders the provider: `CpAdminAppView`, inside `StepUpProvider` and around
 * `AuthenticatedShell`, with the session values of `useAuth`. Who reads it: `UserMenu` (the
 * "Acting as" trigger and the "Act as" group), `ElevationNotice` (the K8 notice and the renew),
 * `NotAuthorizedPanel` (the cause line and the "Switch role" button), `ZeroAccessPanel` and
 * `AuthenticatedShell`. Outside a provider, and for a token with no `heldSiteRoles` claim,
 * `useActiveRole()` answers `null`, and every reader renders as it did before the active role
 * existed. A CP whose K1 gate is off mints such a token, the union token (R-027, R-029).
 *
 * What it owns:
 *   1. The switch: `switchRole(role)` runs `POST /v1/auth/session/active-role` through
 *      `runWithStepUp`, so an elevation opens the real `StepUpDialog` and a switch down asks for
 *      nothing. It answers how the switch ended; it never throws.
 *   2. The expiry: at `elevatedUntilMs`, and when the window gains focus after that instant, it
 *      refreshes the session, so the claims follow the server and the pages re-gate. The server
 *      expiry is the truth; the browser clock only decides when to ask (direction section 8.9).
 *   3. The ended notice: when an elevation ends with no switch from this tab, `ended` names the
 *      role and the instant, so the menu and the 403 panel can say why a page closed.
 *   4. The menu request: `requestRoleMenu()` asks `UserMenu` to open on the "Act as" group.
 *   5. The last switch: `switched` names the role and the instant of the last switch from this tab,
 *      so the 403 panel can say that the person's own switch closed the page.
 *   6. The renew: `renewElevation()` sends the active role again while elevated, through the same
 *      passkey step-up. The server restarts the elevation lifetime (K8). It sets no `switched`.
 *
 * Side effects: one timer per elevation, one focus listener while elevated, and the requests of
 * the switch and the refresh.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { activeRoleSwitchRefusalBodySchema } from '@zion/sdk-contracts';
import { ApiError } from '../platform/http/client';
import { StepUpNotApprovedError, StepUpRepeatedError } from '../platform/auth/step-up';
import { SessionResponseError } from '../platform/auth/auth.api';
import { SwitchNotSentError } from '../platform/auth/useAuth';
import { useStepUp } from '../platform/auth/StepUpDialog';
import { roleName } from './role-names';

/** How one switch ended for the operator. */
export type RoleSwitchResult =
  /** The session now acts as the chosen role. */
  | { readonly kind: 'switched' }
  /** The operator closed the passkey dialog. The role did not change. */
  | { readonly kind: 'cancelled' }
  /** The step-up ended without an approval (denied, expired, used). The role did not change. */
  | { readonly kind: 'not-verified' }
  /** The request failed (a refusal, a network fault, a 5xx). The role did not change. */
  | { readonly kind: 'failed'; readonly message: string };

/** An elevation that ended while this tab was open, with no switch from this tab. */
export interface EndedElevation {
  /** The role the session was elevated to. */
  readonly role: string;
  /** The end of the elevation, epoch ms, from the claim the session held. */
  readonly atMs: number;
}

/** The last switch from this tab that succeeded. */
export interface SwitchedRole {
  /** The role the switch moved to. */
  readonly role: string;
  /** The instant of the switch, epoch ms, from the provider's clock. */
  readonly atMs: number;
}

/** The value of the context. */
export interface ActiveRoleValue {
  /** The operator id of the session, or `null`. */
  readonly operatorId: string | null;
  /** The held site roles, sorted by code unit as the server lists them. */
  readonly heldSiteRoles: readonly string[];
  /** The active site role, or `null` for a person who holds no site role. */
  readonly activeRole: string | null;
  /**
   * The default role of the session (the `restSiteRole` claim, R-004), or `null` when the token
   * names none. The session returns to it at the end of an elevation.
   */
  readonly restRole: string | null;
  /** The end of the elevation, epoch ms, or `null` when the session is not elevated. */
  readonly elevatedUntilMs: number | null;
  /** The last elevation that ended in this tab, or `null`. A switch clears it. */
  readonly ended: EndedElevation | null;
  /** The last switch from this tab that succeeded, or `null`. */
  readonly switched: SwitchedRole | null;
  /** Run one switch to a held role. It never throws. */
  readonly switchRole: (role: string) => Promise<RoleSwitchResult>;
  /**
   * Renew the elevation (K8): send the active role again with a passkey step-up. The server answers
   * a new elevation end. The result says how the renew ended. It never throws, and it changes neither
   * `switched` nor `ended`.
   */
  readonly renewElevation: () => Promise<RoleSwitchResult>;
  /** The provider's clock, epoch ms, so a reader times its notice on the same clock. */
  readonly now: () => number;
  /** A counter that `requestRoleMenu` bumps; `UserMenu` opens on the "Act as" group on a change. */
  readonly menuRequest: number;
  /** Ask the account menu to open on the "Act as" group. */
  readonly requestRoleMenu: () => void;
}

const ActiveRoleContext = createContext<ActiveRoleValue | null>(null);

/** The active-role value, or `null` outside a provider. */
export function useActiveRole(): ActiveRoleValue | null {
  return useContext(ActiveRoleContext);
}

/** The props of {@link ActiveRoleProvider}: the session values and the two calls of `useAuth`. */
export interface ActiveRoleProviderProps {
  /** `auth.operatorId`. */
  readonly operatorId: string | null;
  /** `auth.heldSiteRoles`. `null` (a token without the claim) renders no provider value. */
  readonly heldSiteRoles: readonly string[] | null;
  /** `auth.siteRoles`: the active role alone when the token carries `heldSiteRoles`. */
  readonly siteRoles: readonly string[];
  /** `auth.restSiteRole`. Absent: `null`. */
  readonly restSiteRole?: string | null;
  /** `auth.elevatedUntilMs`. */
  readonly elevatedUntilMs: number | null;
  /** `auth.switchActiveRole`. */
  readonly switchActiveRole: (role: string, stepUpRequestId: string | null) => Promise<void>;
  /** `auth.refreshSession`. */
  readonly refreshSession: () => Promise<void>;
  /** The clock, epoch ms. A test passes its own. */
  readonly now?: () => number;
  readonly children: React.ReactNode;
}

/** The longest single `setTimeout` delay a browser honours (2^31 - 1 ms). */
const MAX_TIMER_MS = 2_147_483_647;

/** The wait before the next refresh when a refresh at the elevation end left the claims unchanged. */
export const REFRESH_RETRY_MS = 30_000;

/** The message of a renew with no active role. No reader offers that renew. */
export const NO_ACTIVE_ROLE_MESSAGE = 'No site role is active, so there is no elevation to renew.';

/**
 * The message of a failed switch, for the status line and the notice: a second step-up, one of the
 * two coded refusals of the switch route, another HTTP status, a 200 body that is not a session, a
 * switch that was not sent (`SwitchNotSentError`), or a request with no answer.
 */
export function failureMessage(cause: unknown): string {
  if (cause instanceof StepUpRepeatedError) return 'The server asked for a second passkey.';
  if (cause instanceof ApiError) {
    // A coded 403 of the switch route names its cause (`ActiveRoleSwitchRefusalBody`).
    const refusal = activeRoleSwitchRefusalBodySchema.safeParse(cause.body);
    if (refusal.success && refusal.data.error === 'role_not_held') return 'You do not hold this role now.';
    if (refusal.success) return `The passkey approval could not be used (${String(refusal.data.reason)}).`;
    return `The server answered ${String(cause.status)}.`;
  }
  // A 200 whose body is not a session: the server answered, so the request did reach it.
  if (cause instanceof SessionResponseError) return 'The server answered with a session the console could not read.';
  if (cause instanceof SwitchNotSentError) return cause.message;
  return 'The request did not reach the server.';
}

/**
 * Provide the active role to the shell. It renders its children with no value when the token
 * carries no `heldSiteRoles` claim, so a CP that mints no active role shows no switch.
 */
export function ActiveRoleProvider({
  operatorId,
  heldSiteRoles,
  siteRoles,
  restSiteRole = null,
  elevatedUntilMs,
  switchActiveRole,
  refreshSession,
  now = Date.now,
  children,
}: ActiveRoleProviderProps): React.JSX.Element {
  const runWithStepUp = useStepUp('role-elevation');
  const activeRole = siteRoles[0] ?? null;
  const [ended, setEnded] = useState<EndedElevation | null>(null);
  const [switched, setSwitched] = useState<SwitchedRole | null>(null);
  const [menuRequest, setMenuRequest] = useState(0);
  const previous = useRef<{ readonly operatorId: string | null; readonly role: string | null; readonly until: number | null }>({
    operatorId,
    role: activeRole,
    until: elevatedUntilMs,
  });
  const nowRef = useRef(now);
  nowRef.current = now;

  /*
   * An elevation that ends at most one second before its end instant, or later, is an expiry: name
   * it. A switch down from this tab earlier than that stays unnamed by the time check.
   */
  useEffect(() => {
    const before = previous.current;
    previous.current = { operatorId, role: activeRole, until: elevatedUntilMs };
    // Another operator signed in: the notices of the previous operator leave with them.
    if (before.operatorId !== operatorId) {
      setEnded(null);
      setSwitched(null);
      return;
    }
    if (before.until !== null && before.role !== null && elevatedUntilMs === null && nowRef.current() >= before.until - 1000) {
      setEnded({ role: before.role, atMs: before.until });
    }
  }, [operatorId, activeRole, elevatedUntilMs]);

  // At the end of the elevation, ask the server; the answer re-gates the pages.
  useEffect(() => {
    if (elevatedUntilMs === null) return undefined;
    let live = true;
    /*
     * A refresh that answers with new claims replaces this effect. One that leaves the claims as
     * they were (a transient failure) asks again after REFRESH_RETRY_MS while the end has passed.
     */
    // The wait until one second after the end, capped, so a capped timer arms again until the end.
    const untilEnd = (): number => Math.min(MAX_TIMER_MS, Math.max(0, elevatedUntilMs - nowRef.current() + 1000));
    const attempt = (): void => {
      const retry = (): void => {
        if (!live) return;
        timer = setTimeout(attempt, nowRef.current() >= elevatedUntilMs ? REFRESH_RETRY_MS : untilEnd());
      };
      refreshSession().then(retry, retry);
    };
    let timer = setTimeout(attempt, untilEnd());
    const onFocus = (): void => {
      if (nowRef.current() >= elevatedUntilMs) void refreshSession();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      live = false;
      clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [elevatedUntilMs, refreshSession]);

  /* One send through the step-up, as a result. It never throws. */
  const send = useCallback(
    async (role: string): Promise<RoleSwitchResult> => {
      try {
        await runWithStepUp((stepUpId) => switchActiveRole(role, stepUpId));
        return { kind: 'switched' };
      } catch (cause) {
        if (cause instanceof StepUpNotApprovedError) {
          return cause.outcome === 'cancelled' ? { kind: 'cancelled' } : { kind: 'not-verified' };
        }
        return { kind: 'failed', message: failureMessage(cause) };
      }
    },
    [runWithStepUp, switchActiveRole],
  );

  const switchRole = useCallback(
    async (role: string): Promise<RoleSwitchResult> => {
      const result = await send(role);
      if (result.kind === 'switched') {
        setEnded(null);
        setSwitched({ role, atMs: nowRef.current() });
      }
      return result;
    },
    [send],
  );

  /* A renew needs an active role; with none the readers offer no renew, and a call answers `failed`. */
  const renewElevation = useCallback(
    (): Promise<RoleSwitchResult> =>
      activeRole === null ? Promise.resolve({ kind: 'failed', message: NO_ACTIVE_ROLE_MESSAGE }) : send(activeRole),
    [send, activeRole],
  );
  const clock = useCallback(() => nowRef.current(), []);

  const requestRoleMenu = useCallback(() => setMenuRequest((n) => n + 1), []);

  const value = useMemo<ActiveRoleValue | null>(
    () =>
      heldSiteRoles === null
        ? null
        : {
            operatorId,
            heldSiteRoles,
            activeRole,
            restRole: restSiteRole,
            elevatedUntilMs,
            ended,
            switched,
            switchRole,
            renewElevation,
            now: clock,
            menuRequest,
            requestRoleMenu,
          },
    [
      operatorId,
      heldSiteRoles,
      activeRole,
      restSiteRole,
      elevatedUntilMs,
      ended,
      switched,
      switchRole,
      renewElevation,
      clock,
      menuRequest,
      requestRoleMenu,
    ],
  );
  return <ActiveRoleContext.Provider value={value}>{children}</ActiveRoleContext.Provider>;
}

/** The wall clock `HH:MM` of an instant, in the browser's zone. */
export function clockOf(ms: number): string {
  const at = new Date(ms);
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

/** The name of an elevation in the menu and the notice (K8): the role name, "Admin" for `platform_admin`. */
export function elevationName(role: string | null): string {
  return actingAsText(role);
}

/** The role name in prose: "Developer" for `app_developer`, or "no site role" for a person who holds none (K2). */
export function actingAsText(role: string | null): string {
  return role === null ? 'no site role' : roleName(role);
}
